import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { Sun, Moon, CheckCircle, Shield, Smartphone, BarChart3, ArrowLeft, EyeOff, Eye } from 'lucide-react';
import fullLogo from '../img/fulllogo.png';
import shortLogo from '../img/shortlogo.png';

const features = [
  { icon: Shield, label: 'Asset QR Management', desc: 'Scan-to-work with auto-linked job queues' },
  { icon: Smartphone, label: 'Mobile Technician View', desc: 'Capture evidence & complete jobs in field' },
  { icon: BarChart3, label: 'PDF Reports & Analytics', desc: 'Instant professional reports with one click' },
];

export default function ResetPasswordPage() {
  const { isDark, toggle } = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast.error('Invalid or missing reset token');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return toast.error('Passwords do not match');
    }
    setLoading(true);
    try {
      const api = require('../utils/api').default;
      await api.post('/auth/reset-password', { token, new_password: password });
      toast.success('Password has been reset successfully');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">
      <style>{`
        .login-root {
          min-height: 100vh;
          display: flex;
          background: var(--bg);
          font-family: var(--font-body);
          position: relative;
          overflow: hidden;
        }
        @media (max-width: 768px) {
          .login-left { display: none !important; }
          .login-right {
            background: #0F172A !important;
            padding: 24px !important;
            position: relative;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow-y: auto;
          }
          .login-right::before {
            content: '';
            position: absolute;
            inset: 0;
            background: radial-gradient(circle at 0% 0%, rgba(14,165,233,0.15) 0%, transparent 50%),
                        radial-gradient(circle at 100% 100%, rgba(139,92,246,0.1) 0%, transparent 50%);
            pointer-events: none;
          }
          .mobile-hero {
            display: flex !important;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 32px;
            animation: fadeIn 0.8s ease-out;
            position: relative;
            z-index: 2;
          }
          .login-card {
            background: rgba(255, 255, 255, 0.03) !important;
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
            padding: 32px 24px !important;
            border-radius: 24px !important;
            box-shadow: 0 20px 50px rgba(0,0,0,0.3) !important;
            width: 100% !important;
            max-width: 400px !important;
            position: relative;
            z-index: 2;
          }
          .mobile-logo {
            width: 58px;
            height: 58px;
            background: linear-gradient(135deg, #0EA5E9, #0284C7);
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 16px;
            box-shadow: 0 8px 16px rgba(14,165,233,0.4);
          }
          .login-title { color: #fff !important; }
          .login-sub { color: rgba(255,255,255,0.5) !important; }
          .form-label { color: rgba(255,255,255,0.7) !important; }
          .form-input { 
            background: rgba(255,255,255,0.05) !important; 
            border-color: rgba(255,255,255,0.1) !important; 
            color: #fff !important;
          }
          .form-input:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px rgba(14,165,233,0.2) !important; }
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .data-stream {
          position: absolute;
          left: -100%;
          width: 200%;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--accent), transparent);
          opacity: 0.3;
          animation: flowStream 10s infinite linear;
        }
        @keyframes flowStream {
          from { transform: translateX(0); }
          to { transform: translateX(50%); }
        }
        .floating-element {
          position: absolute;
          animation: float 6s infinite ease-in-out;
          z-index: 1;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>

      {/* Left Panel */}
      <div className="login-left" style={{
        width: '52%', background: '#0F172A',
        display: 'flex', flexDirection: 'column',
        padding: '48px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.4,
          background: 'radial-gradient(ellipse at 20% 50%, #0EA5E920 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, #8B5CF620 0%, transparent 50%)'
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
          <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={shortLogo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>FieldOps-Pro</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase' }}>Operations Platform</div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontSize: 44, fontWeight: 900, color: '#FFFFFF', lineHeight: 1.1, letterSpacing: -1.5, marginBottom: 20 }}>
            Intelligent<br /><span style={{ color: '#0EA5E9' }}>Field Operations</span><br />Management
          </h1>
          <p style={{ color: '#94A3B8', fontSize: 16, lineHeight: 1.7, maxWidth: 400, marginBottom: 40 }}>
            From asset QR scanning to PDF job completion reports — manage your entire field workflow in one platform.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {features.map(({ icon: Icon, label, desc }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 40, height: 40, background: 'rgba(14,165,233,0.12)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} color="#0EA5E9" />
                </div>
                <div>
                  <div style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 700 }}>{label}</div>
                  <div style={{ color: '#64748B', fontSize: 12 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ color: '#334155', fontSize: 12, position: 'relative', zIndex: 1 }}>copyright 2026 FieldOps Pro - Jarsh & Bajillion Labs Brand</div>
      </div>

      {/* Right Panel */}
      <div className="login-right" style={{ 
        flex: 1, display: 'flex', flexDirection: 'column', 
        alignItems: 'center', justifyContent: 'center', 
        padding: '48px', position: 'relative', overflow: 'hidden',
        background: isDark ? '#020617' : '#f0f9ff'
      }}>
        {/* Advanced Technical Background */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          {/* Circuitry Pattern */}
          <svg style={{ position: 'absolute', width: '100%', height: '100%', opacity: isDark ? 0.4 : 0.2 }}>
            <pattern id="circuitry-v2" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
              <path d="M 0 60 L 60 60 M 60 0 L 60 60 M 60 60 L 120 60 M 60 60 L 60 120" fill="none" stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 4" />
              <circle cx="60" cy="60" r="2.5" fill="var(--accent)" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#circuitry-v2)" />
          </svg>

          {/* Glowing Data Streams */}
          <div className="data-stream" style={{ top: '25%', animationDelay: '0s', opacity: 0.5 }} />
          <div className="data-stream" style={{ top: '55%', animationDelay: '-5s', opacity: 0.5 }} />
          <div className="data-stream" style={{ top: '85%', animationDelay: '-10s', opacity: 0.5 }} />

          {/* Overlay mask for focus */}
          <div style={{ 
            position: 'absolute', inset: 0, 
            background: isDark 
              ? 'radial-gradient(circle at center, transparent 30%, #020617 100%)'
              : 'radial-gradient(circle at center, transparent 30%, #f0f9ff 100%)'
          }} />
        </div>

        <button onClick={toggle} className="hide-mobile" style={{ position: 'absolute', top: 24, right: 24, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px', cursor: 'pointer', color: 'var(--text-secondary)', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="mobile-hero" style={{ display: 'none' }}>
           <div className="mobile-logo" style={{ background: 'transparent', boxShadow: 'none' }}>
             <img src={shortLogo} alt="Logo" style={{ width: 48, height: 48, objectFit: 'contain' }} />
           </div>
           <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 900, letterSpacing: -1 }}>FieldOps-Pro</h1>
           <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>Smart Operations Platform</p>
        </div>

        <div className="login-card" style={{ 
          width: '100%', maxWidth: 400, 
          background: 'var(--bg-card)', 
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--border)', 
          padding: '40px',
          borderRadius: 24,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <img src={fullLogo} alt="FieldOps-Pro" style={{ height: 180, width: '100%', objectFit: 'contain', transform: 'scale(1.4)', marginBottom: -60, pointerEvents: 'none' }} />
            <p className="login-sub" style={{ color: 'var(--text-muted)', marginTop: -20, fontSize: 14, position: 'relative', zIndex: 10 }}>Enter your new secure password below</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPw(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input" type={showConfirmPw ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" required style={{ paddingRight: 44 }} />
                <button type="button" onClick={() => setShowConfirmPw(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
              {loading ? <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> : <><span>Reset Password</span><CheckCircle size={17} /></>}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 24, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 20 }}>
            <Link to="/login" style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              <ArrowLeft size={14} /> Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
